import {ApprovalStatusColor, EApprovalStatus} from "../../../enums/EApprovalStatus";
import { getMediaTypeDescription } from "../../../enums/EMediaType";
import {normalizedNumber} from "../../../helpers/utils";
import  i18next from "i18next";

export type TimelineModel = {
    fromDate: Date,
    toDate: Date,
    groupTimelineModels: GroupTimelineModel[]
}

export type GroupTimelineModel = {
    groupId: number;
    groupName: string;
    primaryMeasurement: string;
    totalCost: number;
    totalQuantity: number;
    customerTimelineModels: CustomerTimelineResource[]
}

export type CustomerTimelineResource = {
    order: number;
    customerId: number;
    customerShortDescription: string;
    totalCost: number;
    totalQuantity: number;
    mediaTypeTimelineModels: MediaTypeTimelineResource[]
}

export type MediaTypeTimelineResource = {
    mediaTypeId: number,
    mediaDescription: string,
    cost: number,
    quantity: number,
    euro: number,
    mediaFee: number,
    timelineEventModels: TimelineEvents[]
}

export type TimelineEvents = {
    id: number,
    poNumber: string,
    description: string,
    fromDate: string,
    toDate: string,
    totalEuro: number,
    totalHls: number,
    hasActuals: boolean,
    mediaFee: number,
    approvalStatus: number,
    proposalSent: boolean,
    timelineEventDetails: TimelineEventDetails[]
}

export type TimelineEventDetails = {
    order: number,
    description: string,
    hls: number,
    discount: string
}

export type TimelineMasterWithEvent = {
    group: GroupTimelineModel;
    customer: CustomerTimelineResource;
    mediaType: MediaTypeTimelineResource;
    event: TimelineEvents;
}

export interface ResourceAreaColumn {
    field: string
    headerContent: string
    group?: boolean
    width?: number;
    cellClassNames?: string | string[];
    headerClassNames ?: string | string[];
}

export interface TimelineEvent {
    resourceId: string
    title: string
    start: Date
    end: Date
    backgroundColor?: string
    borderColor?: string
    classNames?: string[]
    extendedProps?: any
    border?: string
}

//| 'EstimateHls' | 'MediaFee'
export interface ResourceItem {
    id: string;
    title: string;
    eventClassNames: string|string[]|undefined;
    customerId: number;
    customerName: string;
    customer: string;
    mediaTypeId: number
    mediaTypeDescription: string;
    groupId: number;
    groupDescription: string;
    pageCost: string;
    euro: string;
    primaryMeasurement: string;
    quantity: string;
    budget: string;
    implemented: string;
    color: string;
    order: number
}

export const resourceAreaColumns = ():Array<ResourceAreaColumn>  => {
    return [
        {field: 'title', headerContent: i18next.t('media_type',{ns:'promotion_timeline',defaultValue:"Media Type"}), group: undefined, width: 60,cellClassNames:["rotate-90","min-height-50px"]},
        {field: 'total', headerContent: i18next.t('total', {ns:'promotion_timeline',defaultValue:"Total"}) +  " €", group: undefined, width: 80, cellClassNames: ["backcolor-resource-total-euro", "justify-cell-end", "min-height-50px", "resource-font-size-1_1em"], headerClassNames: ["backcolor-resource-total-euro", "resource-font-size-1_1em"]},
        {field: 'pageCost', headerContent: 'Media Fee €', group: undefined, width: 80, cellClassNames: ["backcolor-resource-media-fee", "justify-cell-end", "min-height-50px", "resource-font-size-1_1em"], headerClassNames: ["backcolor-resource-media-fee", "resource-font-size-1_1em"]},
        {field: 'euro', headerContent: i18next.t('discount', {ns:'promotion_timeline',defaultValue:"Total"}) + " €", group: undefined, width: 80, cellClassNames: ["backcolor-resource-discount-euro", "justify-cell-end", "min-height-50px", "resource-font-size-1_1em"], headerClassNames: ["backcolor-resource-discount-euro", "resource-font-size-1_1em"]},
        {field: 'quantity', headerContent: i18next.t('total', {ns:'promotion_timeline',defaultValue:"Total"}) + ` Quantity`, group: undefined, width: 80, cellClassNames: ["backcolor-resource-total-hls", "justify-cell-end", "min-height-50px", "resource-font-size-1_1em"], headerClassNames: ["backcolor-resource-total-hls", "resource-font-size-1_1em"]}
    ]
}

export function toResourceItems(timelineModel: TimelineModel | undefined): ResourceItem[] {
    if (!timelineModel)
        return [];

    return timelineModel
        .groupTimelineModels
        .map((g) => ({
            groupDescription: g.groupName,
            groupId: g.groupId,
            primaryMeasurement: g.primaryMeasurement,
            items: g.customerTimelineModels
        }))
        .flatMap((c) => c.items.map(m => ({
            order: m.order,
            group: c,
            customer: m,
        })))
        .sort((a, b) => (a.group.groupId * 100000) - (b.group.groupId * 100000) + a.order - b.order)
        .flatMap((a, i) => a.customer.mediaTypeTimelineModels.map(m => ({
            group: a.group,
            customer: a.customer,
            mediaType: m,
            index: i
        })))
        .map(m => {
            return {
                id: `${m.group.groupId}_${m.customer.customerId}_${m.mediaType.mediaTypeId}`,
                // title: m.mediaType.description,

                title: getMediaTypeDescription(m.mediaType.mediaTypeId) ,
                eventClassNames: [`lane_color_${m.customer.order % 2}`, 'fullcalendar-z-index'],
                customerId: m.customer.customerId,
                customerName: m.customer.customerShortDescription,
                customer: `${m.group.groupDescription} - ${m.customer.customerShortDescription} ${normalizedNumber(m.customer.totalCost, 2)}€ (${normalizedNumber(m.customer.totalQuantity, 0)}${m.group.primaryMeasurement})`,
                mediaTypeId: m.mediaType.mediaTypeId,
                mediaTypeDescription: m.mediaType.mediaDescription,
                groupId: m.group.groupId,
                groupDescription: m.group.groupDescription,
                implemented: '',
                color: "white",
                order: m.index,
                pageCost: normalizedNumber(m.mediaType.mediaFee, 3),
                budget: "",
                total: normalizedNumber(m.mediaType.cost, 2),
                euro: normalizedNumber(m.mediaType.euro, 2),
                primaryMeasurement: m.group.primaryMeasurement,
                quantity: normalizedNumber(m.mediaType.quantity, 0),
            } as ResourceItem
        });
}

export function toTimelineEvents(customerTimelineResource: TimelineModel | undefined): TimelineEvent[] {
    if (!customerTimelineResource)
        return [];
    return customerTimelineResource.groupTimelineModels
        .flatMap(group => group.customerTimelineModels.flatMap(
            customer => customer.mediaTypeTimelineModels.flatMap(
                mediaType => mediaType.timelineEventModels.map(event => ({ group: group, customer: customer, mediaType: mediaType, event } as TimelineMasterWithEvent)
        ))))
        .map(m => {
            const toDate = new Date(m.event.toDate);
            toDate.setDate(toDate.getDate() + 1);
            //
            const endDate = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate())
            //
            const newDate = toDate.getDate() + 1;
            toDate.setDate(newDate);
            //
            const classes: string[] = []
            if (m.event.hasActuals)
                classes.push('with-actual')

            return {
                resourceId: `${m.group.groupId}_${m.customer.customerId}_${m.mediaType.mediaTypeId}`,
                title: m.event.description,
                start: new Date(m.event.fromDate),
                end: endDate,
                backgroundColor: ApprovalStatusColor[m.event.approvalStatus as EApprovalStatus],
                extendedProps: m,
                classNames: classes
            }
        });
}
